import Phaser from 'phaser';
import { ENEMIES } from '../enemies';

export default class PreloadScene extends Phaser.Scene {
  constructor() {
    super({ key: 'PreloadScene' });
  }

  preload() {
    // 加载进度条
    const progressBar = this.add.graphics();
    const progressBox = this.add.graphics();
    progressBox.fillStyle(0x222222, 0.8);
    progressBox.fillRect(
      this.cameras.main.centerX - 160,
      this.cameras.main.centerY - 20,
      320,
      40
    );

    this.load.on('progress', (value: number) => {
      progressBar.clear();
      progressBar.fillStyle(0x3498db, 1);
      progressBar.fillRect(
        this.cameras.main.centerX - 158,
        this.cameras.main.centerY - 18,
        316 * value,
        36
      );
    });
    this.load.on('complete', () => progressBar.destroy());

    // 地牢 Tileset（10×10 网格，每帧 16×16）
    this.load.spritesheet('tileset', 'assets/tileset/dungeon_tileset.png', {
      frameWidth: 16,
      frameHeight: 16,
    });

    // 玩家角色（Pixel Adventure 主角「Virtual Guy」，每帧 32×32）
    this.load.spritesheet('player_idle', 'assets/player/player_idle.png', { frameWidth: 32, frameHeight: 32 });
    this.load.spritesheet('player_run', 'assets/player/player_run.png', { frameWidth: 32, frameHeight: 32 });
    this.load.spritesheet('player_hit', 'assets/player/player_hit.png', { frameWidth: 32, frameHeight: 32 });

    // ── 怪物（20 种，来自 PixelSRPG-Forge 的 monster 素材）──────
    // 每种怪的 idle/run/attack/hit 都是一张横向序列图
    for (const def of ENEMIES) {
      for (const sh of def.sheets) {
        this.load.spritesheet(sh.key, `assets/${sh.file}`, {
          frameWidth: sh.fw,
          frameHeight: sh.fh,
        });
      }
    }
    // 敌方子弹 / 命中碎片
    this.load.image('bullet_enemy', 'assets/monsters/bullet_enemy.png');
    this.load.spritesheet('hit_pieces', 'assets/monsters/hit_pieces.png', {
      frameWidth: 16,
      frameHeight: 16,
    });

    // 物品
    this.load.image('bullet', 'assets/items/bullet.png');
    this.load.image('potion', 'assets/items/potion.png');
    this.load.image('chest', 'assets/items/chest.png');
    this.load.image('chest_open', 'assets/items/chest_open.png');
    // 金币（4 帧旋转动画，逐帧单独贴图）
    this.load.image('coin_0', 'assets/items/coin_1.png');
    this.load.image('coin_1', 'assets/items/coin_2.png');
    this.load.image('coin_2', 'assets/items/coin_3.png');
    this.load.image('coin_3', 'assets/items/coin_4.png');
  }

  create() {
    this.makePlayerAnims();
    this.makeMonsterAnims();
    this.makeCoinAnim();
    this.makeFxTextures();
    this.scene.start('MenuScene');
  }

  /**
   * 玩家角色动画（每个部件一张横向序列图，每帧 32×32）：
   *   player_idle.png → 待机  player_run.png → 移动  player_hit.png → 受伤
   * 帧数由贴图宽度推导，避免硬编码。
   */
  private makePlayerAnims() {
    const mk = (key: string, tex: string, rate: number, repeat: number) => {
      if (this.anims.exists(key)) return;
      if (!this.textures.exists(tex)) return;
      const src = this.textures.get(tex).getSourceImage() as HTMLImageElement;
      const count = Math.max(1, Math.floor(src.width / 32));
      if (count < 2) return;
      this.anims.create({
        key,
        frames: this.anims.generateFrameNumbers(tex, { start: 0, end: count - 1 }),
        frameRate: rate,
        repeat,
      });
    };
    mk('player-idle', 'player_idle', 10, -1);
    mk('player-run', 'player_run', 14, -1);
    mk('player-hit', 'player_hit', 12, 0); // 受伤：只播一次
  }

  /** 金币旋转动画 */
  private makeCoinAnim() {
    if (this.anims.exists('coin-spin')) return;
    if (!this.textures.exists('coin_0')) return;
    this.anims.create({
      key: 'coin-spin',
      frames: [
        { key: 'coin_0' }, { key: 'coin_1' }, { key: 'coin_2' }, { key: 'coin_3' },
      ],
      frameRate: 9,
      repeat: -1,
    });
  }

  /** 为每种怪的每个部件建立动画 `${texKey}-anim` */
  private makeMonsterAnims() {
    const mk = (texKey: string, fw: number, rate: number) => {
      const animKey = `${texKey}-anim`;
      if (this.anims.exists(animKey)) return;
      const tex = this.textures.get(texKey);
      if (!tex || tex.key === '__MISSING') return;
      const src = tex.getSourceImage() as HTMLImageElement;
      const count = Math.max(1, Math.floor(src.width / fw));
      if (count < 2) return;
      this.anims.create({
        key: animKey,
        frames: this.anims.generateFrameNumbers(texKey, { start: 0, end: count - 1 }),
        frameRate: rate,
        repeat: -1,
      });
    };

    for (const def of ENEMIES) {
      const rate = (name: string) => (name === 'attack' ? 14 : name === 'run' ? 12 : 8);
      (Object.keys(def.anims) as (keyof typeof def.anims)[]).forEach((name) => {
        const texKey = def.anims[name];
        if (!texKey) return;
        const sh = def.sheets.find((s) => s.key === texKey);
        if (sh) mk(texKey, sh.fw, rate(name));
      });
    }
  }

  /** 运行时生成攻击特效贴图：斩击弧、爆炸环、命中火花 */
  private makeFxTextures() {
    if (!this.textures.exists('fx-slash')) {
      const g = this.add.graphics();
      g.lineStyle(7, 0xffffff, 1);
      g.beginPath();
      g.arc(32, 32, 22, Phaser.Math.DegToRad(-55), Phaser.Math.DegToRad(55), false);
      g.strokePath();
      g.generateTexture('fx-slash', 64, 64);
      g.destroy();
    }
    if (!this.textures.exists('fx-ring')) {
      const g = this.add.graphics();
      g.lineStyle(5, 0xffffff, 1);
      g.strokeCircle(32, 32, 28);
      g.generateTexture('fx-ring', 64, 64);
      g.destroy();
    }
    if (!this.textures.exists('fx-spark')) {
      const g = this.add.graphics();
      g.fillStyle(0xffffff, 1);
      g.fillCircle(8, 8, 7);
      g.generateTexture('fx-spark', 16, 16);
      g.destroy();
    }
    // 无敌护盾（圆环刚好填满贴图，便于按角色尺寸精确缩放）
    if (!this.textures.exists('fx-shield')) {
      const g = this.add.graphics();
      const R = 46;
      g.lineStyle(6, 0xffffff, 0.95);
      g.strokeCircle(48, 48, R);
      g.lineStyle(2, 0xffffff, 0.5);
      g.strokeCircle(48, 48, R - 7);
      g.generateTexture('fx-shield', 96, 96);
      g.destroy();
    }
  }
}
